import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Button,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import UploadIcon from "@mui/icons-material/Upload";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { styled } from "@mui/material/styles";
import S3FileUploadService from "../../services/S3FileUploadService";

const DropZone = styled(Box)(({ theme, isDragOver, hasError }) => ({
  border: `2px dashed ${
    hasError
      ? theme.palette.error.main
      : isDragOver
      ? theme.palette.primary.main
      : theme.palette.grey[300]
  }`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(3),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  minHeight: 200,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const FileTile = styled(Box)(({ theme, status }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5), // Reduced from 2
  width: "100%",
  marginBottom: theme.spacing(1), // Reduced from 1.5
  borderRadius: theme.spacing(1), // Reduced from 1.5
  border: `1px solid ${
    status === "completed"
      ? theme.palette.success.main
      : status === "error"
      ? theme.palette.error.main
      : status === "uploading"
      ? theme.palette.primary.main
      : theme.palette.grey[300]
  }`, // Reduced border width from 2px
  background: `linear-gradient(135deg, ${
    status === "completed"
      ? theme.palette.success.light + "08"
      : status === "error"
      ? theme.palette.error.light + "08"
      : status === "uploading"
      ? theme.palette.primary.light + "08"
      : theme.palette.grey[50]
  } 0%, ${theme.palette.background.paper} 100%)`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: `0 2px 8px ${
      status === "completed"
        ? theme.palette.success.main + "15"
        : status === "error"
        ? theme.palette.error.main + "15"
        : status === "uploading"
        ? theme.palette.primary.main + "15"
        : theme.palette.grey[400]
    }`,
  },
}));

const FileIcon = styled(Box)(({ theme, fileType }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1),
  background: `linear-gradient(135deg, ${
    fileType === "image"
      ? theme.palette.info.main
      : fileType === "video"
      ? theme.palette.secondary.main
      : theme.palette.primary.main
  } 0%, ${
    fileType === "image"
      ? theme.palette.info.dark
      : fileType === "video"
      ? theme.palette.secondary.dark
      : theme.palette.primary.dark
  } 100%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: "1.2rem",
  fontWeight: "bold",
  marginRight: theme.spacing(1.5),
  boxShadow: `0 1px 4px ${
    fileType === "image"
      ? theme.palette.info.main + "30"
      : fileType === "video"
      ? theme.palette.secondary.main + "30"
      : theme.palette.primary.main + "30"
  }`,
  overflow: "hidden",
  position: "relative",
}));

const FileStatus = {
  SELECTED: "selected", // New status for files that are selected but not uploaded yet
  PENDING: "pending",
  UPLOADING: "uploading",
  COMPLETED: "completed",
  ERROR: "error",
};

// Separate component for file preview tile
const FilePreviewTile = ({
  fileObj,
  index,
  getFileStatus,
  getFileType,
  getFileIcon,
  formatFileSize,
  handleRemoveFile,
  disabled,
}) => {
  const file = fileObj.file || fileObj;
  const fileStatus = getFileStatus(fileObj);
  const fileType = getFileType(file);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Generate image preview for image files
  React.useEffect(() => {
    if (fileType === "image" && file) {
      // Check if this is a pre-uploaded file with S3 key
      if (file.isPreUploaded && file.s3Key) {
        // Fetch the signed URL for viewing
        setIsLoadingPreview(true);
        S3FileUploadService.getDownloadUrl(file.s3Key)
          .then((response) => {
            if (response.success && response.data?.url) {
              setImagePreview(response.data.url);
              setImageError(false);
            } else {
              setImageError(true);
            }
          })
          .catch((error) => {
            console.error("Error loading image preview:", error);
            setImageError(true);
          })
          .finally(() => {
            setIsLoadingPreview(false);
          });
      } else if (file.size > 0) {
        // Only try to read if file has actual content
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.onerror = () => {
          setImageError(true);
        };
        reader.readAsDataURL(file);
      }
    }
  }, [file, fileType]);

  return (
    <FileTile key={index} status={fileStatus?.status}>
      {/* File Icon or Image Preview */}
      <FileIcon fileType={fileType}>
        {fileType === "image" && imagePreview && !imageError ? (
          <Box
            component="img"
            src={imagePreview}
            alt={file.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 1,
            }}
            onError={() => setImageError(true)}
          />
        ) : (
          getFileIcon(file)
        )}
      </FileIcon>

      {/* File Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "600",
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              mr: 1,
            }}
            title={file.name}
          >
            {file.name}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              {fileType.charAt(0).toUpperCase() + fileType.slice(1)}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: "medium",
              }}
            >
              {formatFileSize(file.size)}
            </Typography>
          </Box>
        </Box>

        {/* Status and Progress */}
        {fileStatus?.status === FileStatus.UPLOADING && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              variant="caption"
              color="primary.main"
              fontWeight="medium"
            >
              Uploading {fileStatus.progress}%
            </Typography>
          </Box>
        )}
        {fileStatus?.status === FileStatus.COMPLETED && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon sx={{ color: "success.main", fontSize: 16 }} />
            <Typography
              variant="caption"
              color="success.main"
              fontWeight="medium"
            >
              Uploaded
            </Typography>
          </Box>
        )}
        {fileStatus?.status === FileStatus.ERROR && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon sx={{ color: "error.main", fontSize: 16 }} />
            <Typography
              variant="caption"
              color="error.main"
              fontWeight="medium"
            >
              Failed
            </Typography>
          </Box>
        )}
        {fileStatus?.status === FileStatus.SELECTED && (
          <Typography
            variant="caption"
            color="secondary.main"
            fontWeight="medium"
          >
            Ready to upload
          </Typography>
        )}

        {/* Progress Bar */}
        {fileStatus?.status === FileStatus.UPLOADING && (
          <LinearProgress
            variant="determinate"
            value={fileStatus.progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "primary.light",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background: `linear-gradient(90deg, primary.main 0%, primary.dark 100%)`,
              },
            }}
          />
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
        {fileStatus?.status === FileStatus.UPLOADING && (
          <CircularProgress
            size={24}
            variant="determinate"
            value={fileStatus.progress}
            sx={{ color: "primary.main" }}
          />
        )}
        {fileStatus?.status === FileStatus.COMPLETED && (
          <CheckCircleIcon sx={{ color: "success.main", fontSize: 28 }} />
        )}
        {(!fileStatus || fileStatus.status !== FileStatus.COMPLETED) &&
          !disabled && (
            <IconButton
              size="small"
              onClick={() => handleRemoveFile(index)}
              sx={{
                color: "error.main",
                "&:hover": {
                  bgcolor: "error.light",
                  color: "error.dark",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
      </Box>
    </FileTile>
  );
};

const S3FileUpload = ({
  value = [],
  onChange,
  caseId = null,
  evidenceId = null,
  multiple = false,
  required = false,
  acceptedFileTypes = ["image/*", "video/*"],
  maxFileSize = 50 * 1024 * 1024, // 50MB to handle large files
  maxFiles = 10,
  label = "Upload Files",
  helperText = "Drag and drop files here or click to browse (Max 50MB per file). Supports images and videos. Files larger than 5MB will be uploaded using multipart upload.",
  error = false,
  disabled = false,
  sx = {},
  onUploadComplete,
  onUploadError,
  autoUpload = true, // New prop to control auto upload
  uploadButtonText = "Upload Files", // Text for upload button
  showUploadButton = false, // Show upload button when autoUpload is false
  keepCompletedFiles = true, // New prop to keep completed files in the list
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState([]);
  const [uploadStates, setUploadStates] = useState(new Map());
  const fileInputRef = useRef();

  const validateFile = useCallback(
    (file) => {
      const newErrors = [];

      // Check file type
      const isValidType = acceptedFileTypes.some((type) => {
        if (type.endsWith("/*")) {
          const baseType = type.split("/")[0];
          return file.type.startsWith(baseType);
        }
        return file.type === type;
      });

      if (!isValidType) {
        newErrors.push(
          `${
            file.name
          }: Invalid file type. Allowed types: ${acceptedFileTypes.join(", ")}`
        );
      }

      // Check file size
      if (file.size > maxFileSize) {
        newErrors.push(
          `${file.name}: File size exceeds ${(
            maxFileSize /
            1024 /
            1024
          ).toFixed(0)}MB limit`
        );
      }

      return newErrors;
    },
    [acceptedFileTypes, maxFileSize]
  );

  const validateFiles = useCallback(
    (files) => {
      const newErrors = [];
      const currentFileCount = value.length;

      // Check total number of files
      if (multiple && currentFileCount + files.length > maxFiles) {
        newErrors.push(
          `Maximum ${maxFiles} files allowed. You have ${currentFileCount} files and trying to add ${files.length} more.`
        );
      }

      // Check for duplicate files
      files.forEach((file) => {
        // Check if file already exists based on name, size, and last modified date
        const isDuplicate = value.some((existingFileObj) => {
          const existingFile = existingFileObj.file || existingFileObj;
          return (
            existingFile.name === file.name &&
            existingFile.size === file.size &&
            existingFile.lastModified === file.lastModified
          );
        });

        if (isDuplicate) {
          newErrors.push(`${file.name}: This file has already been added`);
        } else {
          // Validate individual files only if not duplicate
          const fileErrors = validateFile(file);
          newErrors.push(...fileErrors);
        }
      });

      return newErrors;
    },
    [multiple, maxFiles, validateFile, value]
  );

  const uploadFileToS3 = useCallback(
    async (file, fileId) => {
      try {
        // Update status to uploading
        setUploadStates(
          (prev) =>
            new Map(
              prev.set(fileId, {
                status: FileStatus.UPLOADING,
                progress: 0,
                file: file,
              })
            )
        );

        // Get pre-signed URL
        const urlResponse = await S3FileUploadService.getPresignedUrl({
          caseId,
          evidenceId,
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
        });

        if (!urlResponse.success) {
          throw new Error(urlResponse.message || "Failed to get upload URL");
        }

        const { url, key, multipart } = urlResponse.data;

        // Prepare upload data - for multipart uploads, pass the full response data
        const uploadData = multipart ? urlResponse.data : url;

        // Upload file to S3 using the appropriate method
        const uploadResponse = await S3FileUploadService.uploadToS3(
          uploadData,
          file,
          (progress) => {
            setUploadStates(
              (prev) =>
                new Map(
                  prev.set(fileId, {
                    ...prev.get(fileId),
                    progress,
                  })
                )
            );
          }
        );

        if (!uploadResponse.success) {
          throw new Error("Failed to upload file to S3");
        }

        // Update status to completed
        setUploadStates(
          (prev) =>
            new Map(
              prev.set(fileId, {
                status: FileStatus.COMPLETED,
                progress: 100,
                file: file,
                key: key,
                url: multipart ? null : url ? url.split("?")[0] : null, // Handle multipart uploads
              })
            )
        );

        // Call success callback
        if (onUploadComplete) {
          onUploadComplete(file, key, fileId);
        }

        return { success: true, key, file };
      } catch (error) {
        console.error("Upload error:", error);

        // Update status to error
        setUploadStates(
          (prev) =>
            new Map(
              prev.set(fileId, {
                status: FileStatus.ERROR,
                progress: 0,
                file: file,
                error: error.message,
              })
            )
        );

        // Call error callback
        if (onUploadError) {
          onUploadError(file, error.message, fileId);
        }

        return { success: false, error: error.message };
      }
    },
    [caseId, evidenceId, onUploadComplete, onUploadError]
  );

  const handleFiles = useCallback(
    async (files) => {
      const fileArray = Array.from(files);
      const validationErrors = validateFiles(fileArray);

      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors([]);

      // Create file objects with upload states
      const newFiles = fileArray.map((file) => {
        const fileId = `${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        // Initialize upload state - use SELECTED for manual upload, PENDING for auto upload
        const initialStatus = autoUpload
          ? FileStatus.PENDING
          : FileStatus.SELECTED;
        setUploadStates(
          (prev) =>
            new Map(
              prev.set(fileId, {
                status: initialStatus,
                progress: 0,
                file: file,
              })
            )
        );

        return { file, fileId };
      });

      // Update value immediately to show files in UI
      const updatedValue = multiple
        ? [...value, ...newFiles]
        : newFiles.slice(0, 1);

      console.log("Adding files:", {
        multiple,
        currentValueLength: value.length,
        newFilesLength: newFiles.length,
        updatedValueLength: updatedValue.length,
        newFiles: newFiles.map((f) => f.file.name),
      });

      onChange(updatedValue);

      // Start uploads only if autoUpload is enabled
      if (autoUpload) {
        newFiles.forEach(({ file, fileId }) => {
          uploadFileToS3(file, fileId);
        });
      }
    },
    [value, onChange, multiple, validateFiles, uploadFileToS3, autoUpload]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles, disabled]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (files.length > 0) {
        handleFiles(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = "";
    },
    [handleFiles]
  );

  const handleRemoveFile = useCallback(
    (index) => {
      const fileToRemove = value[index];

      // Check if file is uploaded - if so, don't allow deletion
      if (fileToRemove && fileToRemove.fileId) {
        const fileStatus = uploadStates.get(fileToRemove.fileId);
        if (fileStatus?.status === FileStatus.COMPLETED) {
          // Don't allow deletion of uploaded files
          return;
        }

        // Remove from upload states
        setUploadStates((prev) => {
          const newMap = new Map(prev);
          newMap.delete(fileToRemove.fileId);
          return newMap;
        });
      }

      // Remove from value array
      const newFiles = value.filter((_, i) => i !== index);
      onChange(newFiles);
    },
    [value, onChange, uploadStates]
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // Handle manual upload button click
  const handleUploadClick = useCallback(() => {
    const pendingFiles = value.filter((fileObj) => {
      const status = uploadStates.get(fileObj.fileId);
      return status?.status === FileStatus.SELECTED;
    });

    pendingFiles.forEach(({ file, fileId }) => {
      uploadFileToS3(file, fileId);
    });
  }, [value, uploadFileToS3, uploadStates]);

  const isImage = (file) => file.type.startsWith("image/");
  const isVideo = (file) => file.type.startsWith("video/");

  const getFileType = (file) => {
    if (isImage(file)) return "image";
    if (isVideo(file)) return "video";
    return "document";
  };

  const getFileIcon = (file) => {
    const fileType = getFileType(file);
    if (fileType === "image") return <ImageIcon />;
    if (fileType === "video") return <VideoFileIcon />;
    return <DescriptionIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getFileStatus = (fileObj) => {
    if (!fileObj.fileId) return null;

    const uploadState = uploadStates.get(fileObj.fileId);
    if (uploadState) {
      return uploadState;
    }

    // Check if this is a pre-uploaded file
    const file = fileObj.file || fileObj;
    if (file.isPreUploaded && file.s3Key) {
      return {
        status: FileStatus.COMPLETED,
        progress: 100,
        file: file,
        key: file.s3Key,
      };
    }

    return null;
  };

  const renderFilePreview = (fileObj, index) => {
    return (
      <FilePreviewTile
        key={index}
        fileObj={fileObj}
        index={index}
        getFileStatus={getFileStatus}
        getFileType={getFileType}
        getFileIcon={getFileIcon}
        formatFileSize={formatFileSize}
        handleRemoveFile={handleRemoveFile}
        disabled={disabled}
      />
    );
  };

  const hasError =
    error || errors.length > 0 || (required && value.length === 0);

  return (
    <Box sx={sx}>
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={acceptedFileTypes.join(",")}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
        disabled={disabled}
      />

      <DropZone
        isDragOver={isDragOver}
        hasError={hasError}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        sx={{ opacity: disabled ? 0.6 : 1 }}
      >
        <CloudUploadIcon
          sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {value.length > 0
            ? `${value.length} file(s) selected`
            : "File Upload"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {value.length > 0 ? "Click to add more files" : "No files selected"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {helperText}
        </Typography>
      </DropZone>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              {error}
            </Typography>
          ))}
        </Alert>
      )}

      {value.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {/* Files being uploaded or pending */}
          {value.filter((fileObj) => {
            const status = getFileStatus(fileObj);
            return status?.status !== FileStatus.COMPLETED;
          }).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="600" sx={{ mb: 1 }}>
                Uploading / Pending
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  maxHeight: 300,
                  overflowY: "auto",
                  border: `1px solid`,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  padding: 1,
                  "&::-webkit-scrollbar": {
                    width: 8,
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "grey.100",
                    borderRadius: 4,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "grey.400",
                    borderRadius: 4,
                    "&:hover": {
                      background: "grey.600",
                    },
                  },
                }}
              >
                {value
                  .filter((fileObj) => {
                    const status = getFileStatus(fileObj);
                    return status?.status !== FileStatus.COMPLETED;
                  })
                  .map((fileObj, index) => {
                    const originalIndex = value.indexOf(fileObj);
                    return renderFilePreview(fileObj, originalIndex);
                  })}
              </Box>
            </Box>
          )}

          {/* Upload Button for manual upload mode */}
          {!autoUpload &&
            showUploadButton &&
            value.some((fileObj) => {
              const status = getFileStatus(fileObj);
              return status?.status === FileStatus.SELECTED;
            }) && (
              <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={handleUploadClick}
                  disabled={disabled}
                  color="primary"
                >
                  {uploadButtonText}
                </Button>
              </Box>
            )}

          {/* Successfully uploaded files */}
          {(() => {
            const completedFiles = value.filter((fileObj) => {
              const status = getFileStatus(fileObj);
              return status?.status === FileStatus.COMPLETED;
            });
            return completedFiles.length > 0 ? (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ mb: 1, color: "success.main" }}
                >
                  Successfully Uploaded ({completedFiles.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    maxHeight: 300,
                    overflowY: "auto",
                    border: `1px solid`,
                    borderColor: "success.light",
                    borderRadius: 1.5,
                    padding: 1,
                    backgroundColor: "success.lighter",
                    "&::-webkit-scrollbar": {
                      width: 8,
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "grey.100",
                      borderRadius: 4,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "grey.400",
                      borderRadius: 4,
                      "&:hover": {
                        background: "grey.600",
                      },
                    },
                  }}
                >
                  {completedFiles.map((fileObj, index) => {
                    const originalIndex = value.indexOf(fileObj);
                    return renderFilePreview(fileObj, originalIndex);
                  })}
                </Box>
              </Box>
            ) : null;
          })()}
        </Box>
      )}

      {required && value.length === 0 && !errors.length && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          This field is required
        </Typography>
      )}
    </Box>
  );
};

export default S3FileUpload;
